import * as React from 'react'

import { Grid } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import Section from 'modules/contracts/components/ContractDetails/Section'
import { getContractType } from 'modules/contracts/lib/contractType'
import { useLegalEntityContractData } from 'modules/contracts/lib/legalEntityContractDataContext'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type Employee from 'modules/employees/store/models/Employee'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import ContractType from './ContractType'
import EndDate from './EndDate'
import StartDate from './StartDate'
import TrialPeriod from './TrialPeriod'

interface Props {
  contractVersion: ContractVersion
  employee: Employee
}

const ContractInformation = ({ contractVersion, employee }: Props) => {
  const canEdit = policyAuthorizations.for('contracts.edit', employee)

  const canSeeContractType = policyAuthorizations.for('contracts.see_contract_type', employee)
  const canSeeEndDate = policyAuthorizations.for('contracts.see_end_date', employee)
  const canSeeStartDate = policyAuthorizations.for('contracts.see_start_date', employee)
  const canSeeTrial = policyAuthorizations.for('contracts.see_trial_period', employee)
  const canSeeTrialEnd = policyAuthorizations.for('contracts.see_trial_period_end_date', employee)

  const legalEntityData = useLegalEntityContractData()
  const contractType = getContractType(contractVersion, legalEntityData)
  const hasTrial = contractVersion.attributes.has_trial_period ?? false
  const showStartDate = canSeeStartDate && (contractVersion.startsOn || canEdit)
  const showEndDate = canSeeEndDate && (contractVersion.endsOn || canEdit)
  const showContractType = canSeeContractType && (contractType || canEdit)

  return (
    <Section icon="person" title={i18n.t('contract_versions.contract_information.title')}>
      <Grid variant="fixed">
        {showStartDate && <StartDate startDate={contractVersion.startsOn} />}
        {showContractType && <ContractType contractType={contractType} />}
        {showEndDate && (
          <EndDate endDate={contractVersion.endsOn} canEdit={canEdit} employee={employee} />
        )}
        {canSeeTrial && (
          <TrialPeriod
            hasTrial={hasTrial}
            canEdit={canEdit}
            canSeeTrialEnd={canSeeTrialEnd}
            trialPeriod={
              hasTrial
                ? stringToDate(contractVersion.attributes.trial_period_ends_on ?? '', 'backend')
                : null
            }
          />
        )}
      </Grid>
    </Section>
  )
}

export default observer(ContractInformation)
